/**
 * Timeline transformation utilities for crop schedule phase visualization
 */

import { parseIntervalToDays } from "~/common/utils/date-utils"
import { WEEK_MS } from "../crop-timeline/timeline-constants"


/**
 * Transform phase data into timeline segments with date ranges
 * Priority: Use actual (started_at, duration) over estimated (est_started_at, est_duration)
 *
 * @param {Object} schedule - Schedule with planting_date
 * @param {Array} phases - Ordered phases (by step)
 * @returns {Array} Phase segments with { phaseId, phaseName, startDate, endDate, isCompleted }
 */
export function calculatePhaseTimeline(schedule, phases) {
  if (!schedule || !phases || phases.length === 0) {
    return []
  }

  const segments = []
  const currentTimestamp = new Date()

  for (const phase of phases) {
    // Priority: actual > estimated (user requirement)
    const startDate = phase.started_at
      ? new Date(phase.started_at)
      : (phase.est_started_at ? new Date(phase.est_started_at) : null)

    if (!startDate) {
      console.warn('Phase missing start date:', phase)
      continue
    }

    const duration = phase.duration
      ? parseIntervalToDays(phase.duration)
      : parseIntervalToDays(phase.est_duration)

    const endDate = new Date(startDate)
    endDate.setDate(endDate.getDate() + duration)

    // Detect completed phases by comparing end date to current date
    const isCompleted = endDate < currentTimestamp

    segments.push({
      phaseId: phase.id,
      phaseName: phase.phase_type?.name || 'Unknown Phase',
      phaseCode: phase.phase_type_code,
      startDate,
      endDate,
      duration,
      isCompleted,
      isActive: phase.is_active,
      cropId: schedule.crop_id,
      step: phase.step
    })
  }

  return segments
}

/**
 * Calculate dynamic date range to fit all schedules
 * Adds 10% padding on both sides
 *
 * @param {Array} scheduleData - Array of { crop, schedule, phases } objects
 * @returns {Object} { minDate, maxDate }
 */
export function calculateDateRange(scheduleData) {
  const allDates = []

  for (const item of scheduleData) {
    if (!item.schedule || !item.phases.length) continue

    const timeline = calculatePhaseTimeline(item.schedule, item.phases)
    timeline.forEach(segment => {
      allDates.push(segment.startDate, segment.endDate)
    })
  }

  if (allDates.length === 0) {
    // Fallback: current year
    const now = new Date()
    return {
      minDate: new Date(now.getFullYear(), 0, 1),
      maxDate: new Date(now.getFullYear(), 11, 31)
    }
  }

  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())))
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())))

  // Add 10% padding
  const range = maxDate - minDate
  const padding = range * 0.1

  return {
    minDate: new Date(minDate.getTime() - padding),
    maxDate: new Date(maxDate.getTime() + padding)
  }
}

/**
 * Get planting date from schedule, falling back to first phase's est_started_at
 * This handles the case where schedule.planting_date is derived from phases
 *
 * @param {Object} item - Schedule data item { schedule, phases }
 * @returns {string|null} ISO date string or null
 */
function getPlantingDate(item) {
  if (!item.schedule) return null
  // Use planting_date if available, otherwise derive from first phase
  return item.schedule.planting_date || item.phases?.[0]?.est_started_at || null
}

/**
 * Sort schedule data by earliest planting date
 * Empty schedules go to bottom
 *
 * @param {Array} scheduleData - Array of { crop, schedule, phases }
 * @returns {Array} Sorted scheduleData
 */
export function sortByPlantingDate(scheduleData) {
  return [...scheduleData].sort((a, b) => {
    // Empty schedules to bottom
    if (!a.schedule && !b.schedule) return 0
    if (!a.schedule) return 1
    if (!b.schedule) return -1

    const dateA = getPlantingDate(a)
    const dateB = getPlantingDate(b)

    // Handle missing dates
    if (!dateA && !dateB) return 0
    if (!dateA) return 1
    if (!dateB) return -1

    return new Date(dateA) - new Date(dateB)
  })
}

/**
 * Create crop name to index mapping for Y-axis
 *
 * @param {Array<string>} cropNames - Array of crop names
 * @returns {Object} Map of cropName -> index
 */
export function cropNameToIndex(cropNames) {
  const indexMap = {}
  cropNames.forEach((name, idx) => {
    indexMap[name] = idx
  })
  return indexMap
}

/**
 * Transform phase timeline segments to scatter plot data points
 * Each phase becomes a scatter point with x (start time), y (crop index), and width (duration)
 *
 * @param {Array} scheduleData - Array of { crop, schedule, phases } objects
 * @param {Function} getPhaseColor - Function to get color for phase code
 * @returns {Object} { scatterData, cropNames } - scatterData array and cropNames for Y-axis
 */
export function phasesToScatterData(scheduleData, getPhaseColor) {
  const cropNames = scheduleData.map(item => item.crop.name)
  const cropIndexMap = cropNameToIndex(cropNames)
  const scatterData = []

  for (const item of scheduleData) {
    if (!item.schedule || !item.phases.length) continue

    const cropName = item.crop.name
    const cropIndex = cropIndexMap[cropName]
    const timeline = calculatePhaseTimeline(item.schedule, item.phases)

    for (const segment of timeline) {
      scatterData.push({
        id: segment.phaseId,
        x: segment.startDate.getTime(),  // Timestamp for time scale
        y: cropIndex,                     // Numeric index for band scale
        // Custom properties (scatter allows arbitrary data):
        width: segment.duration * 24 * 60 * 60 * 1000,  // Duration in ms
        phaseCode: segment.phaseCode,
        phaseName: segment.phaseName,
        cropName,
        cropId: segment.cropId,
        startDate: segment.startDate,
        endDate: segment.endDate,
        duration: segment.duration,
        isCompleted: segment.isCompleted,
        isActive: segment.isActive,
        color: getPhaseColor(segment.phaseCode)
      })
    }
  }

  return { scatterData, cropNames }
}

/**
 * Transform scheduleData to week-based format for the planner
 * Calculates week position relative to the timeline's START_DATE
 *
 * @param {Array} scheduleData - Array of { crop, schedule, phases }
 * @param {boolean} dayPrecision - If true, use day precision (for interactive mode)
 * @param {Date} timelineStartDate - The timeline's START_DATE (dynamic based on frost dates)
 * @param {number} maxWeeks - Maximum number of weeks in timeline
 */
export function scheduleDataToWeekFormat(scheduleData, dayPrecision = false, timelineStartDate = null, maxWeeks = 52) {
  // Fallback to legacy behavior if no start date provided
  const getStartDate = (plantingDate) => {
    if (timelineStartDate) return timelineStartDate
    // Legacy: use schedule year's January 4
    const scheduleYear = new Date(plantingDate).getFullYear()
    return new Date(scheduleYear, 0, 4)
  }

  return sortByPlantingDate(scheduleData)
    .filter(item => item.schedule && item.phases.length > 0)
    .map(item => {
      const timeline = calculatePhaseTimeline(item.schedule, item.phases)

      // Derive planting_date from schedule or first phase
      const plantingDate = getPlantingDate(item)

      if (!plantingDate) {
        console.warn('No planting date for schedule:', item.schedule?.id)
        return null
      }

      const startDate = getStartDate(plantingDate)

      const phases = timeline.map(segment => {
        // Match raw phase to get actual vs estimate data separately
        const rawPhase = item.phases.find(p => p.step === segment.step)
        const hasActual = !!(rawPhase?.started_at && rawPhase?.duration)
        const hasStartedAt = !!rawPhase?.started_at
        const hasDuration = !!rawPhase?.duration

        // Compute estimate overlay position in weeks (only meaningful when has_actual)
        let est_start_weeks = null
        let est_len_weeks = null
        if (hasActual && rawPhase.est_started_at && rawPhase.est_duration) {
          const estStartMs = new Date(rawPhase.est_started_at) - startDate
          const estDurationDays = parseIntervalToDays(rawPhase.est_duration)
          if (dayPrecision) {
            est_start_weeks = estStartMs / (WEEK_MS)
            est_len_weeks = estDurationDays / 7
          } else {
            est_start_weeks = Math.floor(estStartMs / (WEEK_MS))
            est_len_weeks = Math.ceil(estDurationDays / 7)
          }
        }

        if (dayPrecision) {
          // Day precision: calculate exact day position and length
          const daysFromStart = (segment.startDate - startDate) / (24 * 60 * 60 * 1000)
          const startInWeeks = daysFromStart / 7
          const lenInWeeks = segment.duration / 7

          return {
            type: segment.phaseCode,
            start: Math.max(0, Math.min(maxWeeks, startInWeeks)), // Allow fractional weeks
            len: Math.max(1 / 7, lenInWeeks), // Minimum 1 day
            label: segment.phaseName,
            // Preserve original phase data for drag interactions
            phase_type_code: segment.phaseCode,
            est_started_at: segment.startDate.toISOString(),
            est_duration: `${segment.duration} days`,
            step: segment.step,
            // Actual timing / reporting fields
            phaseId: segment.phaseId,
            has_actual: hasActual,
            has_started_at: hasStartedAt,
            has_duration: hasDuration,
            est_start_weeks,
            est_len_weeks,
          }
        } else {
          // Week precision (original behavior for dashboard)
          const weekFromStart = Math.floor((segment.startDate - startDate) / (WEEK_MS))

          return {
            type: segment.phaseCode,
            start: Math.max(0, Math.min(maxWeeks - 1, weekFromStart)), // Clamp to timeline range
            len: Math.max(1, Math.ceil(segment.duration / 7)),
            label: segment.phaseName,
            // Preserve original phase data for drag interactions
            phase_type_code: segment.phaseCode,
            est_started_at: segment.startDate.toISOString(),
            est_duration: `${segment.duration} days`,
            step: segment.step,
            // Actual timing / reporting fields
            phaseId: segment.phaseId,
            has_actual: hasActual,
            has_started_at: hasStartedAt,
            has_duration: hasDuration,
            est_start_weeks,
            est_len_weeks,
          }
        }
      })

      return {
        ...item.crop,
        zone: item.crop.template_crop_varieties?.hardiness_zone
          ? `Zone ${item.crop.template_crop_varieties.hardiness_zone}`
          : 'N/A',
        phases
      }
    })
    .filter(Boolean)  // Remove null entries from missing planting dates
}
